import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../components/index.js";
import {
  Field, Select, SectionLabel, FieldLabel, FieldError,
  SOURCE_TYPES, WINDSOR_SOURCE_MAP, SOURCE_CONFIG_FIELD, DEFAULT_TEMPLATE,
} from "../components/AdminForm.jsx";
import { apiFetch } from "../lib/api.js";

const STEPS = ["Business", "Windsor Key", "Data Sources", "Users", "Review"];
const PLANS = [
  { value: "insights", label: "Prism Insights" },
  { value: "professional", label: "Prism Professional" },
];
const ROLES = [
  { value: "OWNER", label: "Owner" },
  { value: "CLIENT", label: "Client (read-only)" },
  { value: "SALES", label: "Sales" },
];

function slugify(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48);
}

function newSource() {
  return { type: "ga4", windsorSourceId: WINDSOR_SOURCE_MAP.ga4, config: {}, fieldTemplate: DEFAULT_TEMPLATE.ga4, test: null };
}

export function NewClient() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const step = Math.min(5, Math.max(1, parseInt(searchParams.get("step") || "1", 10) || 1));
  const setStep = (s) => setSearchParams({ step: String(s) });

  const [slugEdited, setSlugEdited] = useState(false);
  const [form, setForm] = useState({
    name: "", slug: "", website: "", industry: "", location: "",
    accentColor: "#ff6804", plan: "insights", windsorApiKey: "",
    sources: [newSource()],
    users: [{ email: "", name: "", role: "OWNER" }],
  });
  const [busy, setBusy] = useState(false);
  const [launchError, setLaunchError] = useState(null);

  const slug = slugEdited ? form.slug : slugify(form.name);
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  // ── source helpers ──
  const updSource = (i, patch) =>
    setForm((f) => ({ ...f, sources: f.sources.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }));
  const changeSourceType = (i, type) =>
    updSource(i, { type, windsorSourceId: WINDSOR_SOURCE_MAP[type] || type, fieldTemplate: DEFAULT_TEMPLATE[type] || "", config: {}, test: null });
  const addSource = () => setForm((f) => ({ ...f, sources: [...f.sources, newSource()] }));
  const removeSource = (i) => setForm((f) => ({ ...f, sources: f.sources.filter((_, idx) => idx !== i) }));

  // ── user helpers ──
  const updUser = (i, patch) =>
    setForm((f) => ({ ...f, users: f.users.map((u, idx) => (idx === i ? { ...u, ...patch } : u)) }));
  const addUser = () => setForm((f) => ({ ...f, users: [...f.users, { email: "", name: "", role: "SALES" }] }));
  const removeUser = (i) => setForm((f) => ({ ...f, users: f.users.filter((_, idx) => idx !== i) }));

  const canContinue = {
    1: form.name.trim().length > 0,
    2: true, // key optional at this stage; sources can be tested/synced later
    3: true,
    4: form.users.length > 0 && form.users.every((u) => u.email.trim() && u.name.trim()),
    5: true,
  }[step];

  function buildConfig(s) {
    const cfg = { ...s.config };
    if (s.fieldTemplate) cfg.fieldTemplate = s.fieldTemplate;
    return cfg;
  }

  async function launch() {
    setBusy(true);
    setLaunchError(null);
    try {
      const res = await apiFetch("/api/clients", {
        method: "POST",
        body: JSON.stringify({
          name: form.name, slug, website: form.website, industry: form.industry,
          location: form.location, accentColor: form.accentColor, plan: form.plan,
          windsorApiKey: form.windsorApiKey || undefined,
          dataSources: [], // created individually below so they get config + connected status
          initialUser: form.users[0],
        }),
      });
      const id = res.client.id;

      for (const u of form.users.slice(1)) {
        await apiFetch(`/api/clients/${id}/users`, { method: "POST", body: JSON.stringify(u) });
      }
      for (const s of form.sources) {
        await apiFetch(`/api/clients/${id}/sources`, {
          method: "POST",
          body: JSON.stringify({ type: s.type, windsorSourceId: s.windsorSourceId, config: buildConfig(s) }),
        });
      }
      // Initial sync — best effort; don't block navigation if it errors.
      try { await apiFetch(`/api/sync/run?clientId=${id}`, { method: "POST" }); } catch { /* surfaced on detail */ }

      navigate(`/admin/clients/${id}?created=1`);
    } catch (e) {
      setLaunchError(e.message || "Launch failed — check the fields and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5 max-w-[900px]">
      <Stepper step={step} />
      <Card className="px-6 py-6">
        {step === 1 && <StepBusiness form={form} upd={upd} slug={slug} setSlug={(v) => { setSlugEdited(true); upd({ slug: v }); }} />}
        {step === 2 && <StepWindsorKey form={form} upd={upd} />}
        {step === 3 && (
          <StepSources
            form={form} updSource={updSource} changeSourceType={changeSourceType}
            addSource={addSource} removeSource={removeSource}
          />
        )}
        {step === 4 && <StepUsers form={form} updUser={updUser} addUser={addUser} removeUser={removeUser} />}
        {step === 5 && <StepReview form={form} slug={slug} />}

        {launchError && (
          <div className="mt-4 text-[12px] text-negative rounded px-3 py-2 border" style={{ backgroundColor: "#fef2f2", borderColor: "#fecaca" }}>
            {launchError}
          </div>
        )}

        <div className="flex justify-between items-center pt-5 mt-5 border-t border-border-light">
          <button
            type="button"
            onClick={() => (step === 1 ? navigate("/admin/clients") : setStep(step - 1))}
            className="h-9 px-4 rounded border border-border-default text-[13px] text-text-secondary hover:bg-[var(--color-border-light)] transition-colors"
          >
            {step === 1 ? "Cancel" : "Back"}
          </button>
          {step < 5 ? (
            <button
              type="button"
              disabled={!canContinue}
              onClick={() => setStep(step + 1)}
              className="h-9 px-5 rounded bg-accent text-white text-[13px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={launch}
              className="h-9 px-5 rounded bg-accent text-white text-[13px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {busy ? "Launching…" : "Launch Client"}
            </button>
          )}
        </div>
      </Card>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((label, idx) => {
        const n = idx + 1;
        const done = n < step;
        const active = n === step;
        return (
          <div key={label} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 ${active ? "opacity-100" : "opacity-60"}`}>
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                  active ? "bg-accent text-white" : done ? "bg-accent-bg text-accent" : "bg-app-bg text-text-muted border border-border-default"
                }`}
              >
                {done ? "✓" : n}
              </span>
              <span className="text-[12px] text-text-secondary hidden sm:inline">{label}</span>
            </div>
            {n < STEPS.length && <span className="w-5 h-px bg-border-default" />}
          </div>
        );
      })}
    </div>
  );
}

function StepBusiness({ form, upd, slug, setSlug }) {
  return (
    <div>
      <SectionLabel>Business Info</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
        <Field label="Business Name" required autoFocus value={form.name} onChange={(v) => upd({ name: v })} />
        <Field label="Slug" mono value={slug} onChange={setSlug} help="Auto-generated from name · used in URLs" />
        <Field label="Website" value={form.website} onChange={(v) => upd({ website: v })} placeholder="example.com" />
        <Field label="Industry" value={form.industry} onChange={(v) => upd({ industry: v })} />
        <Field label="Location" value={form.location} onChange={(v) => upd({ location: v })} placeholder="City, ST" />
        <Select label="Plan" value={form.plan} onChange={(v) => upd({ plan: v })} options={PLANS} />
        <div>
          <FieldLabel>Accent Color</FieldLabel>
          <div className="flex items-center gap-2">
            <input type="color" value={form.accentColor} onChange={(e) => upd({ accentColor: e.target.value })}
              className="h-9 w-11 rounded border border-border-default cursor-pointer" />
            <input type="text" value={form.accentColor} onChange={(e) => upd({ accentColor: e.target.value })}
              className="flex-1 h-9 px-3 rounded border border-border-default text-[13px] font-mono focus:outline-none focus:border-accent" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepWindsorKey({ form, upd }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  async function testKey() {
    setTesting(true);
    setResult(null);
    try {
      const r = await apiFetch("/api/windsor/test", {
        method: "POST",
        body: JSON.stringify({ apiKey: form.windsorApiKey, source: "google_analytics_4" }),
      });
      setResult(r);
    } catch (e) {
      setResult({ success: false, warnings: [e.message] });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div>
      <SectionLabel>Windsor API Key</SectionLabel>
      <p className="text-[12px] text-text-muted mb-3">
        The per-client Windsor.ai key. "Test Key" attempts a one-week GA4 pull to confirm it works.
        You can also add it later from the client's Settings tab.
      </p>
      <div className="flex items-end gap-2 max-w-[640px]">
        <div className="flex-1">
          <Field label="Windsor API Key" type="password" mono value={form.windsorApiKey} onChange={(v) => upd({ windsorApiKey: v })} />
        </div>
        <button
          type="button"
          onClick={testKey}
          disabled={!form.windsorApiKey || testing}
          className="h-9 px-4 rounded border border-border-default text-[13px] text-text-secondary hover:bg-[var(--color-border-light)] disabled:opacity-50 transition-colors"
        >
          {testing ? "Testing…" : "Test Key"}
        </button>
      </div>
      {result && <TestResult result={result} />}
    </div>
  );
}

function StepSources({ form, updSource, changeSourceType, addSource, removeSource }) {
  return (
    <div>
      <SectionLabel>Data Sources</SectionLabel>
      <div className="space-y-3">
        {form.sources.map((s, i) => (
          <SourceRow
            key={i} index={i} source={s} apiKey={form.windsorApiKey}
            onType={(t) => changeSourceType(i, t)}
            onChange={(patch) => updSource(i, patch)}
            onRemove={form.sources.length > 1 ? () => removeSource(i) : null}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={addSource}
        className="mt-3 h-8 px-3 rounded border border-border-default text-[12px] text-text-secondary hover:bg-[var(--color-border-light)] transition-colors"
      >
        + Add Another Source
      </button>
    </div>
  );
}

function SourceRow({ index, source, apiKey, onType, onChange, onRemove }) {
  const [testing, setTesting] = useState(false);
  const cfgField = SOURCE_CONFIG_FIELD[source.type];

  async function test() {
    setTesting(true);
    onChange({ test: null });
    try {
      const r = await apiFetch("/api/windsor/test", {
        method: "POST",
        body: JSON.stringify({ apiKey, source: source.windsorSourceId }),
      });
      onChange({ test: r });
    } catch (e) {
      onChange({ test: { success: false, warnings: [e.message] } });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="rounded-card border border-border-default p-4 bg-app-bg">
      <div className="flex items-start justify-between mb-3">
        <span className="text-[12px] font-semibold text-text-secondary">Source {index + 1}</span>
        {onRemove && (
          <button type="button" onClick={onRemove} className="text-[11px] text-text-muted hover:text-negative">Remove</button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
        {/* Metricool is connected from Client Detail (brand picker); the wizard has no picker. */}
        <Select label="Source Type" value={source.type} onChange={onType} options={SOURCE_TYPES.filter((t) => t.value !== "metricool")} />
        <Field label="Windsor Source ID" mono value={source.windsorSourceId} onChange={(v) => onChange({ windsorSourceId: v })} />
        {cfgField && (
          <Field
            label={cfgField.label}
            value={source.config?.[cfgField.key] || ""}
            placeholder={cfgField.placeholder}
            onChange={(v) => onChange({ config: { ...source.config, [cfgField.key]: v } })}
          />
        )}
        <Field label="Field Template" value={source.fieldTemplate} onChange={(v) => onChange({ fieldTemplate: v })} />
      </div>
      <div className="mt-3">
        <button
          type="button"
          onClick={test}
          disabled={!apiKey || testing}
          className="h-8 px-3 rounded border border-border-default text-[12px] text-text-secondary hover:bg-surface disabled:opacity-50 transition-colors"
        >
          {testing ? "Testing…" : "Test Connection"}
        </button>
        {!apiKey && <span className="ml-2 text-[11px] text-text-muted">Add a Windsor key (step 2) to test.</span>}
        {source.test && <TestResult result={source.test} />}
      </div>
    </div>
  );
}

function StepUsers({ form, updUser, addUser, removeUser }) {
  return (
    <div>
      <SectionLabel>Invite Users</SectionLabel>
      <p className="text-[12px] text-text-muted mb-3">
        Each user gets a welcome email with a link to set their own password. At least one is required.
      </p>
      <div className="space-y-3">
        {form.users.map((u, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_auto] gap-x-3 gap-y-2 items-end rounded-card border border-border-default p-3 bg-app-bg">
            <Field label="Email" type="email" required value={u.email} onChange={(v) => updUser(i, { email: v })} />
            <Field label="Name" required value={u.name} onChange={(v) => updUser(i, { name: v })} />
            <Select label="Role" value={u.role} onChange={(v) => updUser(i, { role: v })} options={ROLES} />
            {form.users.length > 1 ? (
              <button type="button" onClick={() => removeUser(i)} className="h-9 px-3 text-[11px] text-text-muted hover:text-negative">Remove</button>
            ) : <span />}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addUser}
        className="mt-3 h-8 px-3 rounded border border-border-default text-[12px] text-text-secondary hover:bg-[var(--color-border-light)] transition-colors"
      >
        + Add Another User
      </button>
    </div>
  );
}

function StepReview({ form, slug }) {
  return (
    <div className="space-y-4">
      <SectionLabel>Review &amp; Launch</SectionLabel>
      <ReviewBlock title="Business">
        <ReviewRow k="Name" v={form.name} />
        <ReviewRow k="Slug" v={slug} />
        <ReviewRow k="Website" v={form.website || "—"} />
        <ReviewRow k="Industry" v={form.industry || "—"} />
        <ReviewRow k="Location" v={form.location || "—"} />
        <ReviewRow k="Plan" v={form.plan} />
        <ReviewRow k="Windsor Key" v={form.windsorApiKey ? "Set" : "Not set"} />
      </ReviewBlock>
      <ReviewBlock title={`Data Sources (${form.sources.length})`}>
        {form.sources.map((s, i) => (
          <ReviewRow key={i} k={s.type} v={`${s.windsorSourceId}${s.test ? (s.test.success ? ` · ${s.test.recordCount} rows` : " · test failed") : ""}`} />
        ))}
      </ReviewBlock>
      <ReviewBlock title={`Users (${form.users.length})`}>
        {form.users.map((u, i) => (
          <ReviewRow key={i} k={`${u.name || "—"} (${u.role})`} v={u.email} />
        ))}
      </ReviewBlock>
      <p className="text-[12px] text-text-muted">
        Launching creates the client, its sources and users (welcome emails sent), and triggers an initial sync.
      </p>
    </div>
  );
}

function ReviewBlock({ title, children }) {
  return (
    <div className="rounded-card border border-border-default overflow-hidden">
      <div className="px-4 py-2 bg-app-bg text-[11px] uppercase text-text-muted font-semibold" style={{ letterSpacing: "0.5px" }}>{title}</div>
      <div className="px-4 py-2 divide-y divide-border-light">{children}</div>
    </div>
  );
}

function ReviewRow({ k, v }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-[12px]">
      <span className="text-text-muted">{k}</span>
      <span className="text-text-primary font-medium">{v}</span>
    </div>
  );
}

function TestResult({ result }) {
  const ok = result.success && (result.recordCount ?? 0) > 0;
  return (
    <div className={`mt-2 text-[12px] rounded px-3 py-2 border ${ok ? "text-positive" : "text-negative"}`}
      style={{ backgroundColor: ok ? "#f0fdf4" : "#fef2f2", borderColor: ok ? "#bbf7d0" : "#fecaca" }}>
      {ok
        ? `✓ Connected — ${result.recordCount} record${result.recordCount === 1 ? "" : "s"} in the last 7 days.`
        : `✗ ${result.warnings?.[0] || "No data returned."}`}
      {ok && result.sample?.[0] && (
        <div className="mt-1 font-mono text-[10px] text-text-muted break-all">{JSON.stringify(result.sample[0])}</div>
      )}
    </div>
  );
}
